import { boolean, event, number, oneOf, string, z } from "../schema-util";

// Shared properties for CTS events
const versionId = string().describe("ID of the version");

const ctsBaseProps = {
  versionId,
};
const ctsEvent = event(ctsBaseProps);
export type CtsBaseEventProps = z.infer<typeof ctsEvent>;

export const collaborationCts = {
  // Panel events
  "collaboration.cts.panel.opened": ctsEvent.extend({
    hasExistingConfiguration: boolean().describe("Whether a configuration already exists"),
    canSetupCTS: boolean().describe("Whether the user has permission to setup CTS"),
    canAnalyseCTS: boolean().describe("Whether the user has permission to analyze CTS"),
  }),

  // Setup modal events
  "collaboration.cts.setup.opened": ctsEvent.extend({
    isEditing: boolean().describe("Whether editing an existing configuration"),
    isReadOnly: boolean().describe("Whether the setup is in read-only mode"),
  }),
  "collaboration.cts.setup.cancelled": ctsEvent.extend({
    currentStep: string().describe("The step the user was on when closing"),
    isReadOnly: boolean().describe("Whether the setup is in read-only mode"),
  }),

  // File upload events
  "collaboration.cts.files.uploaded": ctsEvent.extend({
    fileCount: number().describe("Number of files uploaded"),
    fileTypes: string().array().describe("Array of file extensions"),
    automaticMatches: number().describe("Number of files automatically matched"),
    unmatchedFiles: number().describe("Number of files that couldn't be auto-matched"),
  }),
  "collaboration.cts.files.auto_matched": ctsEvent.extend({
    matchedType: oneOf(["layersConfiguration", "costBreakdown", "bom", "spliceTables"]).describe("The type the file was matched to"),
  }),
  "collaboration.cts.files.manual_mapping.opened": ctsEvent.extend({
    unmatchedCount: number().describe("Number of unmatched files requiring manual mapping"),
  }),
  "collaboration.cts.files.manual_mapping.confirmed": ctsEvent.extend({
    mappingCount: number().describe("Number of files manually mapped"),
  }),
  "collaboration.cts.files.validation.failed": ctsEvent.extend({
    fileType: oneOf(["layersConfiguration", "costBreakdown", "bom", "spliceTables"]).describe("The type of file that failed validation"),
    error: string().describe("Error message"),
  }),

  // Layer selection events
  "collaboration.cts.layers.auto_mapped": ctsEvent.extend({
    totalLayers: number().describe("Total number of layers to map"),
    autoMappedCount: number().describe("Number of layers successfully auto-mapped"),
  }),
  "collaboration.cts.layers.mapping_changed": ctsEvent.extend({
    c2sLayerName: string().describe("Name of the C2S layer being mapped"),
    selectedLayerKey: string().optional().describe("Key of the selected project layer"),
    wasIgnored: boolean().describe("Whether the layer was set to be ignored"),
  }),
  "collaboration.cts.layers.reset_from_config": ctsEvent.extend({
    mappedLayerCount: number().describe("Number of previously mapped layers reset from the layers configuration"),
  }),

  // Configuration events
  "collaboration.cts.config.occupancy_type_changed": ctsEvent.extend({
    newType: oneOf(["layer", "file"]).describe("The new occupancy type selected"),
  }),
  "collaboration.cts.config.occupancy_file.uploaded": ctsEvent.describe("User uploaded an occupancy file"),

  // Configuration save events
  "collaboration.cts.configuration.saved": ctsEvent.extend({
    isNew: boolean().describe("Whether this is a new configuration or an update"),
    hasOccupancyFile: boolean().describe("Whether occupancy is provided via file upload"),
    useDefaultBins: boolean().describe("Whether default bins are being used"),
    binCount: number().nullable().describe("Number of bins configured (null if using defaults)"),
    layerMappingsCount: number().describe("Number of layers mapped"),
  }),

  // Generate events
  "collaboration.cts.generate.started": ctsEvent.extend({
    isRegenerate: boolean().describe("Whether this is regenerating existing outputs"),
  }),
  "collaboration.cts.generate.completed": ctsEvent.extend({
    status: oneOf(["COMPLETE", "ERROR"]).describe("The final status of the workflow"),
  }),

  "collaboration.cts.bom-carve.started": ctsEvent.extend({
    isRegenerate: boolean().describe("Whether this is regenerating existing BOM carve outputs"),
  }),
  "collaboration.cts.bom-carve.completed": ctsEvent.extend({
    status: oneOf(["COMPLETE", "ERROR"]).describe("The final status of the BOM carve workflow"),
  }),
};
